import {
  ADMIN_ROLE,
  FILE_DOWNLOAD_ROLE,
  FILE_UPLOAD_ROLE,
  INSTITUTE_ADMIN_ROLE,
  NUMBER_VERIFY_ROLE,
  NUMER_ADD_ROLE,
  PROJECT_ROLES,
} from "@sperrdatei/dto/roles";

import type { NavigationItem, ParentNavigationItem } from ".";
import type { ActiveSession, Session, SetUserSession } from "../../session";
import type { NavigateFunction } from "react-router";

export type NavigationConfigProps = {
  readonly apiFetch: typeof fetch;
  readonly userSession: Session;
  readonly setUserSession: SetUserSession;
  readonly navigate: NavigateFunction;
};

const getRootNavigationItem = ({
  apiFetch,
  userSession,
  setUserSession,
  navigate,
}: NavigationConfigProps): ParentNavigationItem => ({
  itemType: "parent",
  basePath: "/",
  description: "Navigation",
  items: [
    {
      itemType: "parent",
      basePath: "numbers/",
      description: "Sperrdatei",
      items: [
        {
          itemType: "link",
          description: "Nummern sperren",
          path: "add",
          authorizedRoles: [NUMER_ADD_ROLE.key],
        },
        {
          itemType: "link",
          description: "Nummern überprüfen",
          path: "check",
          authorizedRoles: [NUMBER_VERIFY_ROLE.key],
        },
        {
          itemType: "link",
          description: "Nummern bearbeiten",
          path: "edit",
          authorizedRoles: [
            INSTITUTE_ADMIN_ROLE.key,
            NUMER_ADD_ROLE.key,
            FILE_UPLOAD_ROLE.key,
          ],
        },
        {
          itemType: "link",
          description: "Nummer freigeben",
          path: "free",
          authorizedRoles: [],
        },
        {
          itemType: "link",
          description: "Datei upload",
          path: "upload",
          authorizedRoles: [FILE_UPLOAD_ROLE.key],
        },
        {
          itemType: "link",
          description: "Block prüfen",
          path: "block/verify",
          authorizedRoles: [],
        },
        {
          itemType: "link",
          description: "Download",
          path: "download",
          authorizedRoles: [FILE_DOWNLOAD_ROLE.key],
        },
      ],
    },
    {
      itemType: "parent",
      basePath: "account",
      description: "Account",
      items: [],
    },
    {
      itemType: "parent",
      basePath: "help/",
      description: "Hilfe",
      items: [
        {
          itemType: "link",
          description: "Allgemein",
          path: "general",
          authorizedRoles: PROJECT_ROLES.map((role) => role.key),
        },
        {
          itemType: "link",
          description: "Technische Benutzer",
          path: "technical-user",
          authorizedRoles: PROJECT_ROLES.map((role) => role.key),
        },
      ],
    },
    {
      itemType: "parent",
      basePath: "administration/",
      description: "Administration",
      items: [
        {
          itemType: "link",
          description: "Institute Anzeigen",
          path: "institutes/list",
          authorizedRoles: [],
        },
        {
          itemType: "link",
          description: "Institut anlegen",
          path: "institutes/add",
          authorizedRoles: [],
        },
        {
          itemType: "link",
          description: "Institut bearbeiten",
          path: "institutes/edit",
          authorizedRoles: [INSTITUTE_ADMIN_ROLE.key],
        },
        {
          itemType: "link",
          description: "Vorwahl hinzufügen",
          path: "prefix/add",
          authorizedRoles: [],
        },
      ],
    },

    {
      itemType: "action",
      description: "Logout",
      authorizedRoles: PROJECT_ROLES.map((role) => role.key),
      action: async (): Promise<void> => {
        try {
          if (userSession.active) {
            await apiFetch(`/api/login/session/${userSession.sessionId}`, {
              method: "DELETE",
            });
          }
        } finally {
          setUserSession({ active: false });
          navigate("/");
        }
      },
    },
  ],
});

const filterUnauthorizedNavItems = (
  item: NavigationItem,
  userSession: ActiveSession,
): NavigationItem | undefined => {
  if (item.itemType === "parent") {
    const items = item.items
      .map((child) => filterUnauthorizedNavItems(child, userSession))
      .filter((item) => !!item);
    return items.length
      ? {
          ...item,
          items,
        }
      : undefined;
  }

  if (userSession.roles.some((role) => role === ADMIN_ROLE.key)) {
    return item;
  }

  return userSession.roles.some((role) => item.authorizedRoles.includes(role))
    ? item
    : undefined;
};

export const createNavigationItems = ({
  apiFetch,
  userSession,
  setUserSession,
  navigate,
}: NavigationConfigProps): ParentNavigationItem => {
  const root = getRootNavigationItem({
    apiFetch,
    userSession,
    setUserSession,
    navigate,
  });

  if (!userSession.active) {
    return {
      ...root,
      items: [],
    };
  }

  const filteredRoot = filterUnauthorizedNavItems(root, userSession);

  if (!filteredRoot || filteredRoot.itemType !== "parent") {
    return {
      ...root,
      items: [],
    };
  }

  return filteredRoot;
};
